/**
 * Keyset management for Cashero.
 *
 * A keyset is a set of private/public key pairs, one per denomination (power-of-2 amounts).
 * The keyset ID is derived from the public keys and unit, identifying which keys
 * signed each proof.
 */

import { secp256k1 } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex, hexToBytes, randomBytes } from '@noble/hashes/utils';
import { utf8ToBytes } from '@noble/hashes/utils';

import type { Point } from './blind-signature';
import { bytesToScalar, pointFromHex } from './blind-signature';

/** The unit for all Cashero keysets */
export const KEY_UNIT = 'piconero';

/** Default version byte for keyset ID derivation */
export const KEY_VERSION_BYTE = 1;

/**
 * Maximum number of denominations (power-of-2 amounts).
 * 2^64 = ~18.4 billion piconero ≈ 0.018 XMR — sufficient for daily use.
 * For larger amounts, multiple proofs are combined.
 */
export const DEFAULT_DENOMINATION_COUNT = 32; // up to 2^31 piconero ≈ 2.1 XMR

export const MIN_DENOMINATION_COUNT = 8;  // up to 2^7 = 128 piconero
export const MAX_DENOMINATION_COUNT = 48; // up to 2^47 piconero ≈ 140 XMR

/** Public keyset (shared with wallets) */
export interface Keyset {
  /** The keyset ID (hex, version-prefixed) */
  id: string;
  /** The unit this keyset is denominated in */
  unit: string;
  /** Public keys per denomination amount: amount -> hex pubkey */
  pubkeys: Record<number, string>;
  /** When this keyset was created */
  createdAt: number;
  /** Whether this keyset is active (mint may rotate to new keysets) */
  active: boolean;
}

/** Private keyset (mint-side only, never shared) */
export interface KeysetPrivate extends Keyset {
  /** Private keys per denomination amount: amount -> hex privkey */
  privkeys: Record<number, string>;
}

/** Options for keyset creation */
export interface CreateKeysetOptions {
  denominationCount?: number;
  seed?: Uint8Array;
  unit?: string;
  versionByte?: number;
}

/**
 * Derive the keyset ID from public keys.
 *
 * Follows NUT-02 v1 format:
 *   preimage = amount:pubkey,amount:pubkey,...|unit:unit
 *   id = "01" + sha256(preimage).slice(0, 14)
 */
export function deriveKeysetId(pubkeys: Record<number, string>, unit: string = KEY_UNIT, versionByte: number = KEY_VERSION_BYTE): string {
  const sorted = Object.entries(pubkeys)
    .sort(([a], [b]) => Number(a) - Number(b));

  const preimage = sorted
    .map(([amount, pubkey]) => `${amount}:${pubkey.toLowerCase()}`)
    .join(',') + `|unit:${unit.toLowerCase()}`;

  const hash = sha256(utf8ToBytes(preimage));
  const hashHex = bytesToHex(hash).slice(0, 14);
  return `01${hashHex}`;
}

/**
 * Create a new keyset with random key pairs for each denomination.
 *
 * Denominations are powers of 2: 2^0, 2^1, 2^2, ..., 2^(count-1) piconero.
 */
export function createNewKeyset(options: CreateKeysetOptions = {}): KeysetPrivate {
  const count = options.denominationCount ?? DEFAULT_DENOMINATION_COUNT;
  const unit = options.unit ?? KEY_UNIT;
  const versionByte = options.versionByte ?? KEY_VERSION_BYTE;

  const pubkeys: Record<number, string> = {};
  const privkeys: Record<number, string> = {};

  for (let i = 0; i < count; i++) {
    const amount = 2 ** i;
    const privKey = options.seed
      ? deriveDeterministicKey(options.seed, i, versionByte)
      : bytesToHex(crypto.getRandomValues(new Uint8Array(32)));

    const pubKey = secp256k1.getPublicKey(hexToBytes(privKey), true);
    pubkeys[amount] = bytesToHex(pubKey);
    privkeys[amount] = privKey;
  }

  const id = deriveKeysetId(pubkeys, unit, versionByte);

  return {
    id,
    unit,
    pubkeys,
    privkeys,
    createdAt: Math.floor(Date.now() / 1000),
    active: true,
  };
}

/**
 * Derive a deterministic private key for a given index from a seed.
 * Uses BIP32-like derivation (simplified: SHA-256(seed || index)).
 */
function deriveDeterministicKey(seed: Uint8Array, index: number, versionByte: number): string {
  const indexBytes = new Uint8Array(4);
  new DataView(indexBytes.buffer).setUint32(0, index, false);
  const hash = sha256(new Uint8Array([...seed, ...indexBytes]));
  // Ensure it's a valid secp256k1 scalar
  const scalar = bytesToScalar(hash);
  const hex = scalar.toString(16).padStart(64, '0');
  return hex;
}

/**
 * Get the public key point for a given amount from a keyset.
 */
export function getPubkeyForAmount(keyset: Keyset, amount: number): Point | null {
  const pubkeyHex = keyset.pubkeys[amount];
  if (!pubkeyHex) return null;
  try {
    return pointFromHex(pubkeyHex);
  } catch {
    return null;
  }
}

/**
 * Get the private key bytes for a given amount from a private keyset.
 */
export function getPrivkeyForAmount(keyset: KeysetPrivate, amount: number): Uint8Array | null {
  const privkeyHex = keyset.privkeys[amount];
  if (!privkeyHex) return null;
  try {
    return hexToBytes(privkeyHex);
  } catch {
    return null;
  }
}

/**
 * Serialize a keyset to JSON-safe format (for transport/storage).
 */
export function serializeKeyset(keyset: Keyset): string {
  return JSON.stringify({
    id: keyset.id,
    unit: keyset.unit,
    pubkeys: keyset.pubkeys,
    createdAt: keyset.createdAt,
    active: keyset.active,
  });
}

/**
 * Parse a serialized keyset.
 */
export function parseKeyset(json: string): Keyset {
  const data = JSON.parse(json);
  return {
    id: data.id,
    unit: data.unit ?? KEY_UNIT,
    pubkeys: data.pubkeys,
    createdAt: data.createdAt,
    active: data.active ?? true,
  };
}
